import { Component, ContentChild, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild, inject } from "@angular/core";
import { UntypedFormGroup, FormsModule, ReactiveFormsModule, FormGroup } from "@angular/forms";

import { of, throwError } from "rxjs";
import { catchError, finalize, first, tap } from "rxjs/operators";

import { DffError, isNumeric } from "@dffedb/util";
import { LoginService } from "@e-forsyning/common/auth";

import { LoginData } from "../login-data.model";
import { LoginHjaelpService } from "../login-hjaelp/login-hjaelp.service";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatButton } from "@angular/material/button";
import { MatomoTracker } from "ngx-matomo-client";
import { LocalStorage } from "@dffedb/util";
import { MatExpansionPanel } from "@angular/material/expansion";
import { RedirectService } from "@e-forsyning/common/router";
import { DffRouteParams } from "@dffedb/router";
import { CommonModule } from "@angular/common";
import { BiometricService } from "../biometrics/biometrics.service";

@Component({
    selector: "login-input",
    templateUrl: "./login-input.html",
    styleUrl: "./login-input.scss",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatCheckbox, MatButton, MatExpansionPanel]
})
export class LoginInput implements OnChanges {
    @Input()
    public parent: UntypedFormGroup;

    @Input() public technicianLogin: boolean = false;

    @Input() public loginDisabled: boolean = false;

    @Input() public technicianContactPhone?: string;

    @Input() public emailPlaceholder?: string;

    @Input() public passwordPlaceholder?: string;

    @Input() public helpLinkText?: string;

    @Input() public showHelpLink: boolean = true;

    @Input() public forgotPasswordText?: string;

    @Input() public firstTimeText?: string;

    @Input() public showFirstTimeLink: boolean = true;

    @Output()
    public loginSucceeded = new EventEmitter<any>();

    @Output()
    public alphaKeyPressed = new EventEmitter<string>();

    @ViewChild("forbrugerNrInput", { static: true }) public forbrugerNrInput: ElementRef;

    @ContentChild("biometricLoginButton") biometricTemplate?: TemplateRef<any>;

    public foersteGangAktiveret = false;
    public glemtKodeordAktiveret = false;
    public arbejder = false;
    public infoBesked = "";
    public fejlBesked = "";
    public visFeltFejl = false;

    private readonly matomoTracker = inject(MatomoTracker);

    constructor(
        private readonly loginService: LoginService,
        private readonly loginHjaelpService: LoginHjaelpService,
        private readonly localStorage: LocalStorage,
        private readonly redirectService: RedirectService,
        private readonly routeParams: DffRouteParams,
        private readonly biometricService: BiometricService
    ) {}

    ngOnInit(): void {
        const grp = this.parent.get("forbrugerNrGroup") as FormGroup;
        const ctrl = grp?.get("forbrugerNr");
        const existing = ctrl.validator ? [ctrl.validator] : [];
        ctrl.setValidators([...existing]);
        ctrl.updateValueAndValidity({ emitEvent: false });
        this.applyLoginDisabledState();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes["loginDisabled"] || changes["parent"]) {
            this.applyLoginDisabledState();
        }
    }

    private applyLoginDisabledState(): void {
        if (!this.parent) return;
        const controls = [
            this.parent.get("forbrugerNrGroup.forbrugerNr"),
            this.parent.get("forbrugerNrGroup.kode"),
            this.parent.get("emailGroup.email"),
            this.parent.get("emailGroup.adgangskode"),
            this.parent.get("huskMig")
        ];

        if (this.loginDisabled) {
            controls.forEach((control) => control?.disable({ emitEvent: false }));
            return;
        }

        controls.forEach((control) => control?.enable({ emitEvent: false }));
    }

    public focus(): void {}

    public loginClick(): void {
        const loginData: LoginData = {
            brugernavn: this.parent.get("emailGroup.email").value,
            kodeord: this.parent.get("emailGroup.adgangskode").value,
            huskMig: this.parent.get("huskMig").value
        };
        console.log("[login-debug] loginClick", {
            technicianLogin: this.technicianLogin,
            hasUsername: !!loginData.brugernavn,
            hasPassword: !!loginData.kodeord,
            huskMig: !!loginData.huskMig
        });
        if (this.technicianLogin) {
            this.onTechnicianLogin(loginData);
            return;
        }
        this.onForbrugerLogin(loginData);
        return;
    }

    public onForbrugerLogin(credentials: LoginData): void {
        this.fejlBesked = "";
        this.arbejder = true;
        console.log("[login-debug] onForbrugerLogin start", { username: credentials.brugernavn });
        this.loginService
            .login(credentials.brugernavn, credentials.kodeord, credentials.huskMig)
            .pipe(
                first(),
                tap(async (result) => {
                    if (result.success) {
                        console.log("[login-debug] onForbrugerLogin success");
                        this.localStorage.setItem("sidste-login-side", "forbruger");
                        const available = await this.biometricService.isAvailable();
                        if (available && !localStorage.getItem("biometricAsked")) {
                            try {
                                await this.biometricService.enableBiometricLogin(
                                    credentials.brugernavn,
                                    credentials.kodeord,
                                    "Aktiver biometrisk login",
                                    "Aktiver biometrisk login",
                                    "Bekræft din identitet for at gemme dine loginoplysninger sikkert. Herefter kan du logge ind hurtigere med finger- eller ansigtsgenkendelse."
                                );
                                localStorage.setItem("biometricAsked", "true");
                                this.loginSucceeded.emit();
                            } catch (err) {
                                localStorage.setItem("biometricAsked", "true");
                                this.loginSucceeded.emit();
                            }
                            this.matomoTracker.trackEvent("Authentication", "Login Success", "Forbrugernummer");
                        } else {
                            this.matomoTracker.trackEvent("Authentication", "Login Success", "Forbrugernummer");
                            this.loginSucceeded.emit();
                        }
                    } else {
                        console.log("[login-debug] onForbrugerLogin failed");
                        this.matomoTracker.trackEvent("Authentication", "Login Failed", "Forbrugernummer - Invalid Credentials");

                        if (this.parent.get("forbrugerNrGroup.forbrugerNr")?.hasError("digitsOrEmail")) {
                            this.fejlBesked = "Forbrugernummer skal være enten kun tal eller en gyldig e-mail adresse";
                        } else {
                            this.fejlBesked = "Ugyldig forbrugernummer, e-mail eller kode";
                        }
                    }
                }),
                catchError((error: DffError) => {
                    console.log("[login-debug] onForbrugerLogin error", error);
                    this.matomoTracker.trackEvent("Authentication", "Login Failed", "Forbrugernummer - Error");
                    this.fejlBesked = error.friendlyMessage || error.message;
                    return throwError(error);
                }),
                finalize(() => {
                    console.log("[login-debug] onForbrugerLogin finalize");
                    this.arbejder = false;
                })
            )
            .subscribe();
    }

    public onTechnicianLogin(credentials: LoginData): void {
        this.fejlBesked = "";
        this.arbejder = true;
        console.log("[login-debug] onTechnicianLogin start", { username: credentials.brugernavn });
        this.loginService
            .login(credentials.brugernavn, credentials.kodeord, credentials.huskMig)
            .pipe(
                first(),
                tap(async (result) => {
                    if (result.success) {
                        console.log("[login-debug] onTechnicianLogin success");
                        this.localStorage.setItem("sidste-login-side", "tekniker");

                        const available = await this.biometricService.isAvailable();
                        if (available && !localStorage.getItem("biometricAsked")) {
                            try {
                                await this.biometricService.enableBiometricLogin(
                                    credentials.brugernavn,
                                    credentials.kodeord,
                                    "Aktiver biometrisk login",
                                    "Aktiver biometrisk login",
                                    "Bekræft din identitet for at gemme dine loginoplysninger sikkert. Herefter kan du logge ind hurtigere med finger- eller ansigtsgenkendelse."
                                );
                                localStorage.setItem("biometricAsked", "true");
                                this.loginSucceeded.emit();
                            } catch (err) {
                                localStorage.setItem("biometricAsked", "true");
                                this.loginSucceeded.emit();
                            }
                            this.matomoTracker.trackEvent("Authentication", "Login Success", "Technician");
                        } else {
                            this.matomoTracker.trackEvent("Authentication", "Login Success", "Technician");
                            this.loginSucceeded.emit();
                        }
                    } else {
                        console.log("[login-debug] onTechnicianLogin failed");
                        this.matomoTracker.trackEvent("Authentication", "Login Failed", "Technician - Invalid Credentials");
                        this.fejlBesked = "Initialer eller e-mail var ikke gyldig";
                    }
                }),
                catchError((error: DffError) => {
                    console.log("[login-debug] onTechnicianLogin error", error);
                    this.matomoTracker.trackEvent("Authentication", "Login Failed", "Technician - Error");
                    this.fejlBesked = error.friendlyMessage || error.message;
                    return throwError(error);
                }),
                finalize(() => {
                    console.log("[login-debug] onTechnicianLogin finalize");
                    this.arbejder = false;
                })
            )
            .subscribe();
    }

    public loginHjaelpClick(): void {
        this.loginHjaelpService.visLoginHjaelpDialog();
    }

    public firstEmailLoginClick(): void {
        const email = this.parent.get("emailGroup.email").value;
        const adgangskode = this.parent.get("emailGroup.adgangskode").value;
        const forbrugerNr = this.parent.get("forbrugerNrGroup.forbrugerNr").value;
        const kode = this.parent.get("forbrugerNrGroup.kode").value;
        this.arbejder = true;
        this.loginService
            .tilknytEmail(forbrugerNr, kode, email, adgangskode)
            .pipe(
                first(),
                tap(() => {
                    this.matomoTracker.trackEvent("Authentication", "Email Setup", "Success");
                    this.fejlBesked = "";
                    this.foersteGangAktiveret = false;
                }),
                catchError((error: any) => {
                    console.log("FEJL VED LOGIN:::", error.error);
                    this.matomoTracker.trackEvent("Authentication", "Email Setup", "Failed");
                    this.fejlBesked = error.error;
                    return of(null);
                }),
                finalize(() => {
                    this.arbejder = false;
                })
            )
            .subscribe();
    }

    public sendGlemtKodeordClick(): void {
        const email = this.parent.get("emailGroup.email").value;
        this.arbejder = true;
        this.loginService
            .glemtKodeord(email)
            .pipe(
                first(),
                tap(() => {
                    this.matomoTracker.trackEvent("Authentication", "Forgot Password", "Success");
                    this.foersteGangAktiveret = false;
                    this.fejlBesked = "";
                    this.infoBesked = `E-mail sendt til: ${email}`;
                }),
                catchError((error: any) => {
                    this.matomoTracker.trackEvent("Authentication", "Forgot Password", "Failed");
                    console.log("FEJL :::", error.error);
                    this.matomoTracker.trackEvent("Authentication", "Forgot Password", "Failed");
                    this.fejlBesked = error.error;
                    return of(null);
                }),
                finalize(() => {
                    this.arbejder = false;
                })
            )
            .subscribe();
    }

    public onGlemtKodeordClick(): void {
        this.glemtKodeordAktiveret = !this.glemtKodeordAktiveret;
        this.fejlBesked = "";
        this.foersteGangAktiveret = false;
        this.infoBesked = "";
    }

    public firstTimeClick(): void {
        this.fejlBesked = "";
        this.foersteGangAktiveret = !this.foersteGangAktiveret;
    }

    public redirectEfterLogin(): void {
        const path = this.routeParams.get("path");
        if (path && path !== "404") {
            this.redirectService.redirect(path);
        } else {
            this.redirectService.redirectTilInstallationer();
        }
    }
}
